import { ListSkeleton } from '@atlas/ui-components'
import { Badge } from '@atlas/ui-primitives'
import { ArrowLeft } from 'lucide-react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArticleLocaleStrip } from '@/components/articles/ArticleLocaleStrip'
import { ArticleTranslateDialog } from '@/components/articles/ArticleTranslateDialog'
import { PageHeader } from '@/components/editorial/PageHeader'
import { ARTICLE_STATUS_LABELS, type Article, type ArticleStatus } from '@/domain/article.types'
import { useArticle, useArticleAvailableActions, useTransitionArticle, useUpdateArticle } from '@/hooks/useArticles'
import { useAxesWithValues } from '@/hooks/useLibrary'
import { apiErrorMessage } from '@/lib/api-error'
import { ArticleComposer } from './ArticleComposer'
import { discardRemovedImages } from './discard-images'
import { fromArticle, reconcileImageFileIds, toUpdateInput } from './form-model'
import { useArticleComposerState } from './useArticleComposerState'
import { useArticleTranslationPanel } from './useArticleTranslationPanel'

/**
 * The composer once the article is known.
 *
 * A separate component so the form is SEEDED from a complete article rather than
 * patched into shape by an effect after mounting — which is what makes "never
 * partially editable" structural instead of a race the loading flag has to win.
 */
function ArticleEditor({ article }: { article: Article }) {
	const navigate = useNavigate()
	const update = useUpdateArticle()
	const transition = useTransitionArticle()
	const { axes } = useAxesWithValues()
	const { data: availableActions = [] } = useArticleAvailableActions(article.articleId)
	const { form, set, imageProps } = useArticleComposerState(fromArticle(article))
	const translations = useArticleTranslationPanel(article)

	const onSubmit = () => {
		// Deleted only once the save has landed: until then the stored article still
		// shows these images, and cancelling must leave them intact.
		const { removedFileIds } = reconcileImageFileIds(form)
		update.mutate(
			{ articleId: article.articleId, patch: toUpdateInput(form) },
			{
				onSuccess: () => {
					void discardRemovedImages(removedFileIds)
					void navigate('/articles')
				},
			},
		)
	}

	// The mutation invalidates the article and its available actions, so the offered
	// transitions refresh themselves. The composer's unsaved body is untouched
	// either way: a refused transition must not cost the author their draft.
	const onTransition = (to: ArticleStatus) => transition.mutate({ articleId: article.articleId, to })

	const failure = update.isError
		? apiErrorMessage(update.error, 'Could not save the article.')
		: transition.isError
			? apiErrorMessage(transition.error, 'Could not change the status.')
			: null

	return (
		<div>
			<Link
				to="/articles"
				className="mb-4 inline-flex items-center gap-1 text-sm text-ds-text-muted hover:text-ds-text"
			>
				<ArrowLeft className="h-4 w-4" /> Articles
			</Link>
			<PageHeader
				eyebrow="Articles / Edit"
				title={article.title}
				subtitle="Update any field; changes save on submit."
				actions={<Badge>{ARTICLE_STATUS_LABELS[article.status]}</Badge>}
			/>
			{/* Above the composer, not below it: the body editor fills the viewport,
			    and a failed translation an author has to scroll past a whole article
			    to notice is one they will not notice. */}
			<ArticleLocaleStrip
				variants={translations.variants}
				currentArticleId={article.articleId}
				labels={translations.labels}
				isLoading={translations.isLoading}
				onManage={translations.onManage}
				source={translations.source}
			/>
			<ArticleTranslateDialog
				isOpen={translations.isDialogOpen}
				offerableLocales={translations.offerableLocales}
				existingVariants={translations.existingVariants}
				labels={translations.labels}
				selected={translations.selected}
				onToggle={translations.toggleLocale}
				sourceContentLength={article.content.length}
				onConfirm={translations.submit}
				onCancel={translations.closeDialog}
				isPending={translations.isRequesting}
				error={translations.requestError}
			/>
			<ArticleComposer
				form={form}
				set={set}
				axes={axes}
				{...imageProps}
				availableActions={availableActions}
				onTransition={onTransition}
				isTransitioning={transition.isPending}
				submitLabel="Save changes"
				isPending={update.isPending}
				onSubmit={onSubmit}
				onCancel={() => void navigate('/articles')}
				error={failure}
			/>
		</div>
	)
}

/** Edit an article (route `/articles/:articleId/edit`). */
export function EditArticlePage() {
	const { articleId } = useParams<{ articleId: string }>()
	const { data: article, isLoading, isError } = useArticle(articleId ?? null)

	if (isLoading) return <ListSkeleton label="Loading…" />
	if (isError || !article) return <p className="text-ds-danger-strong">This article could not be loaded.</p>

	// Keyed on the id so navigating between two articles re-seeds the form rather
	// than carrying the previous one's state into it.
	return <ArticleEditor key={article.articleId} article={article} />
}
